/* eslint-disable max-len */
/* eslint-disable no-restricted-syntax */
import { DataContext } from '@/components/context/DataContext';
import { StatusContext } from '@/components/context/StatusContext';
import Text from 'atoms/Text';
import {
    popupSpecsForDes, priorityOrder, parseFXOInput
} from 'lib/constants';
import debounce from 'lodash.debounce';
import { nanoid } from 'nanoid';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
    useCallback, useContext, useEffect, useRef, useState
} from 'react';
import { useIrsGlobalSearchEnterCommand } from '../hooks/useIrsGlobalSearchEnterCommand';
import { toast } from 'react-toastify';
import { getAuth } from 'services/auth.service';
import { socket, socketConnect, socketEmit } from 'services/socket';
import { trackCommandEvents, eventCommands } from 'lib/events';
import parsePlot from 'lib/getFormattedLegs';
import { ChevronRight } from 'react-feather';
import SearchBox from '../atoms/SearchBox';
import { trackEvent } from '../helper/mixPanel';

const Dictaphone = dynamic(() => import('../components/Dictaphone'), {
    ssr: false
});

const GlobalSearch = ({
    icon, iconClass, options = [], onSelect = () => { }, placeholder, clubUsersIntoFunctions = false, inputClass, forDashboard = true, forDetailsPage = false,
    suggestionBg, categoryTitleClass, categoryList, highlightBg, suggestionName, globalSearchList,
    takeScreenShot, selectedCommand = 'DES', setYasPrice = () => { }, voiceCommand = false,
    curve = '', isPlotPage = false, legs = [], setTicker = () => { },
    isCorpChildComponent = false, isIrsPage = false, isOTCPage = false, irsTerm = '', isBondPage = false,
    immFwd = null, isFwdPlot = false, ovdvCurrency = null, irsId = '',
    isCommandLine = true, hasSpreadDiffStartDate = false, irs = null, commandOverrides = {}
}) => {
    const enableVoiceCommand = true;
    const [searchPhrase, setSearchPhrase] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef(null);
    const [highlightedId, setHighlightedId] = useState('');
    const parentIndex = useRef(0);
    const childIndex = useRef(-1);
    const [commandsAndUsers, setCommandsAndUsers] = useState(options);
    const allSuggestions = [...commandsAndUsers, ...suggestions];
    const { isF3Entered, setIsF3Entered } = useContext(DataContext);
    const searchBoxRef = useRef(null);
    const yasWithPriceRegex = /^yas (\d+(\.\d+)?)$/;
    const matchYasCommandWithPrice = (str) => yasWithPriceRegex.test(str);
    const router = useRouter();
    const openTargetPage = (url) => {
        window.open(url, nanoid(5), popupSpecsForDes);
    };
    const openInSamePage = (url) => {
        window.open(url, '_self');
    };
    const [validImm, setValidImm] = useState([]);
    const [regressData, setRegressData] = useState([]);
    const [allIrs, setAllIrs] = useState([]);
    const valid6s3sBobCcySet = new Set(allIrs.filter((i) => i.basis6s3s || i.bob).map((i) => i.currency));

    const { globalCommand } = useContext(StatusContext);
    const auth = getAuth();

    const getPriceFromYasWithPrice = (str) => {
        const match = str.match(yasWithPriceRegex);
        if (match) {
            return match[1];
        }
        return null;
    };

    function onAllIrsEvent(data) {
        if (data?.error) {
            return;
        }
        setAllIrs(data);
    }
    const parseInput = (text) => {
        if (!text.trim().toUpperCase().startsWith('INP') && !text.trim().toUpperCase().startsWith('BXT') && !text.trim().toUpperCase().startsWith('SXT')) {
            return { isValid: false };
        }
        const reservedStartPattern = /^(spot|[HMUZhmuz]\d{1,2})$/i;

        const tenorPattern = /^\d+[mywd]$/i;
        const dv01Pattern = /\b\d+[k]\b/i;
        const pricePattern = /^\b\d+(\.\d+)?(o|b|p|r|bid|offer|pay|rec|receive)?\b$/i;
        const sizePattern = /\b\d+(Mn|Mm|Mio)\b/i;

        const result = {
            curveName: null,
            start: null,
            tenors: [],
            dv01: null,
            price: null,
            size: null,
            isValid: false
        };

        const parts = text.slice(3).trim().split(/\s+/);

        const curveName = [];
        let remainingParts = [];

        for (const part of parts) {
            if (
                reservedStartPattern.test(part)
                || tenorPattern.test(part)
                || dv01Pattern.test(part)
                || pricePattern.test(part)
                || sizePattern.test(part)
            ) {
                remainingParts = parts.slice(parts.indexOf(part));
                break;
            }
            curveName.push(part);
        }

        result.curveName = curveName.length > 0 ? curveName.join(' ') : null;

        for (const part of remainingParts) {
            if (reservedStartPattern.test(part)) {
                result.start = part.toUpperCase();
                if (result.start === 'SPOT') {
                    result.start = 'Spot';
                }
            } else if (tenorPattern.test(part)) {
                result.tenors.push(part);
            } else if (dv01Pattern.test(part)) {
                result.dv01 = part;
            } else if (pricePattern.test(part)) {
                result.price = part;
            } else if (sizePattern.test(part)) {
                result.size = part;
            }
        }

        const {
            start, dv01, price, tenors, size
        } = result;

        if (result.curveName || start || dv01 || price || size || (tenors.length > 0 && tenors.length < 4)) {
            result.isValid = true;
        }

        if (tenors.length > 3) {
            result.isValid = false;
        }
        if (result.isValid === true && tenors.length > 0) {
            const formattedTenors = tenors.map((tenor) => {
                const value = tenor.replace(/(\d+)\s*([a-zA-Z])/, (match, number, unit) => `${number} ${unit.toUpperCase()}`);
                if (value === '12 M') return '1 Y';
                return value;
            });
            const unitOrder = ['D', 'W', 'M', 'Y'];

            const sortedTenors = formattedTenors.sort((a, b) => {
                const [numA, unitA] = a.split(' ');
                const [numB, unitB] = b.split(' ');

                const unitComparison = unitOrder.indexOf(unitA) - unitOrder.indexOf(unitB);
                if (unitComparison !== 0) return unitComparison;

                return parseInt(numA, 10) - parseInt(numB, 10);
            });
            result.tenors = sortedTenors;
        }

        return result;
    };

    useEffect(() => {
        if (!forDashboard) {
            searchBoxRef.current.focus();
        }
    }, []);

    const getResults = (value) => {
        if (value.toLowerCase().startsWith('inp ') || value.toLowerCase().startsWith('plot ') || value.toLowerCase().startsWith('roll ') || value.toLowerCase().startsWith('basis ') || value.toLowerCase().startsWith('gip ') || value.toLowerCase().startsWith('atlas ') || value.toLowerCase().startsWith('monitor ')) {
            setSuggestions(() => []);
            setHighlightedId(null);
            return;
        }
        if (value?.trim()) socketEmit('/global-search', { phrase: value });
    };
    const getResults2 = (data) => {
        socketEmit('/search/voice', data);
    };
    const debouncedInputHandler = useRef(debounce(getResults, 800, { leading: false }));
    const debouncedInputHandler2 = useRef(debounce(getResults2, 300, { leading: false }));

    const handleInputChange = (e) => {
        setSearchPhrase(e.target.value);
        if (!e.target.value) { setSuggestions(() => []); setCommandsAndUsers(options); return; }
        if (!forDashboard && e.target.value.length) setShowSuggestions(() => true);
        if (e.target.value.length < 2) return;
        debouncedInputHandler.current(e.target.value);
    };

    const getFilteredCommands = (searchPhrase) => options[0].items
        .filter((optionItem) => {
            const isStartsWithPlotf = ((optionItem.command === 'PLOTF')) && searchPhrase.toLowerCase().startsWith('plotf ');
            const isStartsWithPlotA = ((optionItem.command === 'PLOTA')) && searchPhrase.toLowerCase().startsWith('plota ');
            const isStartsWithPlot = ((optionItem.command === 'PLOT') && searchPhrase.toLowerCase().startsWith('plot '));
            const isStartsWithRoll = ((optionItem.command === 'ROLL') && searchPhrase.toLowerCase().startsWith('roll ')) && isIrsPage;
            const isStartsWithRegress = ((optionItem.command === 'REGRESS') && searchPhrase.toLowerCase().startsWith('regress ')) && isIrsPage;
            const isStartsWithTHOR = ((optionItem.command === 'THOR') && searchPhrase.toLowerCase().startsWith('thor ')) && isIrsPage;
            const isStartsWithBasis = ((optionItem.command === 'BASIS') && searchPhrase.toLowerCase().startsWith('basis ')) && isIrsPage;
            const isStartsWithInp = ((optionItem.command === 'INP') && searchPhrase.toLowerCase().startsWith('inp '));
            const isStartsWithOtc = ((optionItem.command === 'OTC') && searchPhrase.toLowerCase().startsWith('otc '));
            const isStartsWithCorp = ((optionItem.command === 'CORP') && searchPhrase.toLowerCase().includes('corp'));
            const isStartsWithBxt = ((optionItem.command === 'BXT') && searchPhrase.toLowerCase().includes('bxt'));
            const isStartsWithSxt = ((optionItem.command === 'SXT') && searchPhrase.toLowerCase().includes('sxt'));
            const isStartsWithGIP = ((optionItem.command === 'GIP') && searchPhrase.toLowerCase().includes('gip')) && isIrsPage;
            const isStartsWithAtlas = ((optionItem.command === 'ATLAS') && searchPhrase.toLowerCase().includes('atlas'));
            const isStartsWithCurve = ((optionItem.command === 'CURVE') && searchPhrase.toLowerCase().startsWith('curve '));
            const isStartsWithTrace = ((optionItem.command === 'TRACE') && searchPhrase.toLowerCase().includes('trace '));
            const isStartsWithAnchor = ((optionItem.command === 'ANCHOR') && searchPhrase.toLowerCase().includes('anchor '));
            const isStartsWith6s3s = ((optionItem.command === '6s3s')) && (searchPhrase.toLowerCase().startsWith('6s3s ') || searchPhrase.toLowerCase().startsWith('63 '));
            const isStartsWithBob = ((optionItem.command === 'bob')) && searchPhrase.toLowerCase().startsWith('bob ');
            const isStartsWithOmon = ((optionItem.command === 'OMON') && searchPhrase.toLowerCase().includes('omon '));
            const isStartsWithWatchlist = ((optionItem.command === 'WATCHLIST') && searchPhrase.toLowerCase().includes('watchlist '));
            const isStartsWithDslice = ((optionItem.command === 'DSLICE') && searchPhrase.toLowerCase().includes('dslice '));

            return (isStartsWithPlot || isStartsWithRegress || isStartsWithRoll || isStartsWithInp || isStartsWithOtc || isStartsWithCorp || isStartsWithBxt || isStartsWithSxt || isStartsWithBasis || isStartsWithAtlas || isStartsWithTHOR || isStartsWithGIP || isStartsWithPlotf || isStartsWithCurve || isStartsWithPlotA || isStartsWithTrace || isStartsWithAnchor || isStartsWith6s3s || isStartsWithBob || isStartsWithOmon || isStartsWithWatchlist || isStartsWithDslice) || optionItem.command.toLowerCase().includes(searchPhrase.trim().toLowerCase())
                || optionItem.label.toLowerCase().includes(searchPhrase.toLowerCase())
                || (optionItem.command.toLowerCase() === 'yas' && matchYasCommandWithPrice(searchPhrase.toLowerCase()));
        });

    const mixPanelEvent = (type, results, ref) => {
        const count = results?.reduce((acc, curr) => acc + curr?.items?.length, 0);
        const searchQuery = ref?.current?.value;
        trackEvent('Search', {
            'Search Query': searchQuery,
            Count: count,
            'User Id': auth.id,
            Type: type
        });
    };
    const byLexical = (a, b) => Number(a.lexicalRank) > Number(b.lexicalRank);
    const filterAndSortBonds = (phrase, items) => {
        const hasPhrase = []; const others = [];
        items.forEach((item) => (item.name.toLowerCase().startsWith(phrase) ? hasPhrase.push(item) : others.push(item)));
        return [...hasPhrase.sort(byLexical), ...others.sort(byLexical)];
    };

    const getPriority = (type) => {
        const idx = priorityOrder.indexOf(type);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    const perfectMatchKeyByType = {
        Bond: 'name',
        MaturedBond: 'name',
        Irs: 'name',
        Cds: 'name',
        Vol: 'name',
        Currency: 'ticker',
        QuickCurrency: 'ticker',
        Ndf: 'ticker',
        Equity: 'name'
    };

    const isPerfectMatch = (item, phrase) => {
        const normalizedPhrase = phrase?.trim().toLowerCase();
        if (!normalizedPhrase) return false;

        const keyToCompare = perfectMatchKeyByType[item?.type] || 'name';
        const value = item?.[keyToCompare];
        return typeof value === 'string' && value.trim().toLowerCase() === normalizedPhrase;
    };

    const onResultsFetch = ({ results, phrase, error }) => {
        if (error) { setSuggestions([]); return; }
        if (!results.length) setSuggestions([]);
        const suggestions = results;
        const indexOfSecurities = suggestions.findIndex((suggestion) => suggestion.category === 'Securities');
        if (indexOfSecurities > -1) {
            const bonds = []; const others = [];
            suggestions[indexOfSecurities].items.forEach((item) => (item.type === 'Bond' ? bonds.push(item) : others.push(item)));
            const sortedBonds = filterAndSortBonds(phrase, bonds);
            const sortedItems = [...sortedBonds, ...others].sort((a, b) => {
                const pa = getPriority(a.type);
                const pb = getPriority(b.type);

                if (pa !== pb) return pa - pb;

                if (a.type !== 'Bond') {
                    if (Number(a.lexicalRank) < Number(b.lexicalRank)) return -1;
                    if (Number(a.lexicalRank) > Number(b.lexicalRank)) return 1;
                    return (Number(a.days) - Number(b.days));
                }
            });
            const perfectMatches = [];
            const nonPerfectMatches = [];
            sortedItems.forEach((item) => (isPerfectMatch(item, phrase) ? perfectMatches.push(item) : nonPerfectMatches.push(item)));
            suggestions[indexOfSecurities].items = [...perfectMatches, ...nonPerfectMatches];
        }
        if (clubUsersIntoFunctions) {
            const userCategory = suggestions.find((suggestion) => suggestion.category === 'Users');
            if (userCategory) {
                const formattedUsers = userCategory.items.map((user) => (
                    {
                        id: user.id, group: 'Users', command: `BIO ${user.name}`, label: ` ${user.companyName}`, type: 'Function'
                    }));
                setSearchPhrase((prevState) => {
                    const formattedCommands = getFilteredCommands(prevState);
                    if (formattedCommands.length || formattedUsers.length) {
                        setCommandsAndUsers([{ category: 'Functions', items: [...formattedCommands, ...formattedUsers] }]);
                    } else {
                        setCommandsAndUsers([]);
                    }
                    return prevState;
                });
            }
            if (searchBoxRef?.current?.value) {
                mixPanelEvent('Command Prompt', results, searchBoxRef);
            }
            setSuggestions(suggestions.filter((suggestion) => suggestion.category !== 'Users'));
            return;
        }
        if (searchBoxRef?.current?.value) {
            mixPanelEvent('Global Search', results, searchBoxRef);
        }

        setSuggestions(suggestions);
    };

    useEffect(() => {
        allSuggestions.every((item, index1) => {
            if (item.category === 'Functions') {
                item.items.every((item2, index2) => {
                    if ((item2.command.toLowerCase() === searchPhrase.toLowerCase())
                        || (item2.command.toLowerCase() === 'yas' && matchYasCommandWithPrice(searchPhrase.toLowerCase()))
                        || (item2.command.toLowerCase() === 'corp' && searchPhrase.toLowerCase().includes('corp'))
                    ) {
                        parentIndex.current = index1;
                        childIndex.current = index2;
                        setHighlightedId(`${allSuggestions[index1].category}-${index2}`);
                        return false;
                    }
                    if (highlightedId) {
                        setHighlightedId(() => '');
                        parentIndex.current = 0;
                        childIndex.current = -1;
                    }

                    return true;
                });
            }
            return true;
        });
    }, [commandsAndUsers]);

    useEffect(() => {
        if (searchPhrase === 'PLOT ' || searchPhrase === 'REGRESS ' || searchPhrase === 'ROLL' || searchPhrase === 'INP ' || searchPhrase === 'OTC' || searchPhrase === 'BASIS ' || searchPhrase === 'ATLAS ' || searchPhrase === 'THOR ' || searchPhrase === 'GIP' || searchPhrase === 'ANCHOR ' || searchPhrase === 'WATCHLIST ' || (searchPhrase === 'CORP' && !isCorpChildComponent)) return;
        if (!searchPhrase.length) {
            setSuggestions(() => []);
            setShowSuggestions(() => false);
            return;
        }
        if (searchPhrase.length) {
            setShowSuggestions(() => true);

            if (forDetailsPage) {
                const filteredCommands = getFilteredCommands(searchPhrase);
                if (filteredCommands.length) {
                    setCommandsAndUsers(() => [{ category: 'Functions', items: filteredCommands }]);
                } else {
                    setCommandsAndUsers([]);
                }
            }
        }
    }, [searchPhrase]);

    const resetCursor = () => {
        childIndex.current = -1;
        parentIndex.current = 0;
        setHighlightedId('');
    };

    const getHighlightedItemData = useCallback(() => {
        const all = [...commandsAndUsers, ...suggestions];
        return all[parentIndex.current]?.items[childIndex.current];
    }, [commandsAndUsers, suggestions]);

    const { executeIrsEnterCommand, runCommand } = useIrsGlobalSearchEnterCommand({
        irs,
        irsId,
        isIrsPage,
        isBondPage,
        curve,
        validImm,
        forDetailsPage,
        isOTCPage,
        isPlotPage,
        legs,
        immFwd,
        isFwdPlot,
        hasSpreadDiffStartDate,
        irsTerm,
        ovdvCurrency,
        valid6s3sBobCcySet,
        setSearchPhrase,
        resetCursor,
        isCorpChildComponent,
        setTicker,
        getHighlightedItemData,
        commandOverrides
    });
    const runCommandRef = useRef(runCommand);
    useEffect(() => {
        runCommandRef.current = runCommand;
    }, [runCommand]);

    const handleClickOutside = (e) => {
        if (inputRef.current && !inputRef.current.contains(e.target)) {
            setShowSuggestions(false);
            !forDashboard && setSearchPhrase(() => '');
            resetCursor();
        }
    };

    const handleRegress = (data) => {
        setRegressData((prev) => [...prev, data]);
    };

    useEffect(() => {
        if (regressData?.[0]?.command?.toLowerCase() === 'anchor') {
            if (regressData.some((d) => d.error)) {
                setRegressData([]);
                return;
            }
            const depCurve = regressData.find((d) => d.uid === 'reg1');
            if (!depCurve) return;
            const url = `${process.env.NEXT_PUBLIC_UI_URL}anchor?l1=${depCurve.term}&depId=${depCurve.id}${depCurve.imm ? '&depImm=1' : ''}${(depCurve.isFwd || depCurve.imm) ? '&depF=1' : ''}`;
            openInSamePage(url);
            setRegressData([]);
        } else if (regressData.length === 2) {
            if (regressData.some((d) => d.error)) {
                setRegressData([]);
                return;
            }
            const { command } = regressData[0];
            const indepCurve = regressData.find((d) => d.uid === 'reg2');
            const depCurve = regressData.find((d) => d.uid === 'reg1');
            if (!depCurve || !indepCurve) return;
            if (command === 'BASIS') {
                const params = {
                    l1: depCurve.term,
                    depId: depCurve.id,
                    l2: indepCurve.term,
                    indepId: indepCurve.id
                };
                if (depCurve.imm) params.depImm = '1';
                if (depCurve.isFwd || depCurve.imm) params.depF = '1';
                if (indepCurve.imm) params.indepImm = '1';
                if (indepCurve.isFwd || indepCurve.imm) params.indepF = '1';
                setRegressData([]);
                setSearchPhrase('');
                console.log('params=======================is', { params });
                runCommand('BASIS', params);
                return;
            }
            const url = `${process.env.NEXT_PUBLIC_UI_URL}${command === 'THOR' ? 'thor' : 'regress'}?l1=${depCurve.term}&depId=${depCurve.id}${depCurve.imm ? '&depImm=1' : ''}${(depCurve.isFwd || depCurve.imm) ? '&depF=1' : ''}&l2=${indepCurve.term}&indepId=${indepCurve.id}${indepCurve.imm ? '&indepImm=1' : ''}${(indepCurve.isFwd || indepCurve.imm) ? '&indepF=1' : ''}`;
            openInSamePage(url);
            setRegressData([]);
        }
    }, [regressData]);

    const onPlotFetch = (data) => {
        if (['reg1', 'reg2'].includes(data.uid)) {
            handleRegress(data);
            return;
        }

        const plotType = { irs: 'irs', currency: 'currencies', cds: 'cds' };
        if (data.error) return;
        if (data.command === 'BXTSXTPAGE') return;
        if (data.command === 'fwcm') {
            if (data.type !== 'irs') return;
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}fwcm/${plotType[data.type]}/${data.id}`);
            return;
        }

        if (data.command === 'edge') {
            if (data.type !== 'irs') return;
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}edge/${plotType[data.type]}/${data.id}`);
            return;
        }

        if (data.command === 'stats') {
            if (data.type !== 'irs') return;
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}stats/${plotType[data.type]}/${data.id}`);
            return;
        }

        if (data.command === 'CURVE') {
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}curve/${data.id}`);
            return;
        }

        if (data.command === 'arc') {
            if (data.type !== 'irs') return;
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}arc/irs/${data.id}`);
            return;
        }

        if (data.command === 'CHRONOS') {
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}chronos/${data.id}?str=${data.structure}`);
            return;
        }
        if (data.command === 'meet') {
            if (data.type !== 'irs') return;
            runCommandRef.current('MEET', { irsId: data?.id });
            return;
        }
        if (data.command === 'INP') {
            let inpUrl = '';
            if (data.type === 'irs') {
                if (!data?.irsSecurityId) return;
                sessionStorage.setItem('inpCmdDetails', JSON.stringify(data));
                window.open(`${process.env.NEXT_PUBLIC_UI_URL}irs/form`, 'irs_form', popupSpecsForDes);
                setSearchPhrase('');
                return;
            }
            if (data.type === 'currency') {
                if (!data?.currencySecurityId) return;
                inpUrl = `${process.env.NEXT_PUBLIC_UI_URL}${plotType[data.type]}/${data.currencySecurityId}?inp=1`;
                sessionStorage.setItem(`${data.currencySecurityId}_input`, JSON.stringify(data));
                sessionStorage.setItem(`${data.irsSecurityId}_input`, JSON.stringify(data));
                openInSamePage(inpUrl);
                return;
            }
        }
        if (data.command === 'BXT') {
            if (!data?.irsSecurityId) return;
            const inpUrl = `${process.env.NEXT_PUBLIC_UI_URL}${plotType[data.type]}/${data.irsSecurityId}?bxt=1`;
            sessionStorage.setItem(`${data.currencySecurityId}_bxt`, JSON.stringify(data));
            sessionStorage.setItem(`${data.irsSecurityId}_bxt`, JSON.stringify(data));
            openInSamePage(inpUrl);
            return;
        }
        if (data.command === 'SXT') {
            if (!data?.irsSecurityId) return;
            const inpUrl = `${process.env.NEXT_PUBLIC_UI_URL}${plotType[data.type]}/${data.irsSecurityId}?sxt=1`;
            sessionStorage.setItem(`${data.currencySecurityId}_sxt`, JSON.stringify(data));
            sessionStorage.setItem(`${data.irsSecurityId}_sxt`, JSON.stringify(data));
            openInSamePage(inpUrl);
            return;
        }

        if (data.command === 'BASIS') {
            if (data.isReferencePlot) {
                runCommandRef.current('BASIS', data);
                setSearchPhrase('');
            }
            return;
        }
        if (data.command === 'PLOTF') {
            if (!data.irsSecurityId) {
                const inpUrl = `${process.env.NEXT_PUBLIC_UI_URL}plotf/${plotType[data.type]}/${data.id}?legs=${data.term}`;
                openInSamePage(inpUrl);
                return;
            }
        }
        let url = `${process.env.NEXT_PUBLIC_UI_URL}plot/${plotType[data.type]}/${data.id}?legs=${data.term}${data.spot ? '&spot=1' : ''}${data.type === 'irs' && data.isFwd ? '&command=fwcm' : ''}${data.imm ? `&immFwd=${data.imm.label}` : ''}${data.thirdImm ? `&immLabel3=${data.thirdImm.label}` : ''}${data.plotType ? `&plotType=${data.plotType}` : ''}${data.secondImm ? `&imm=${data.secondImm.y}&immLabel=${data.secondImm.label}` : ''}`;

        if (data?.term?.length === 4 && ['fSpread', 'fSpreadDiff'].includes(data?.plotType)) {
            url += '&diffStart=1'
        }

        if (data.command === 'PLOTA') {
            url += '&advanced=true';
        }
        if (forDetailsPage) openInSamePage(url);
        else openTargetPage(url);
        setSearchPhrase('');
        resetCursor();
    };

    const onGetCurrencyPairFetch = ({ data, error, command }) => {
        if (error) {
            if (['ovdv', 'omon'].includes(command?.toLowerCase())) toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
            sessionStorage.removeItem('fxoInput');
            return;
        }
        if (data?.command === 'dslice') {
            const url = `${process.env.NEXT_PUBLIC_UI_URL}/fx-options/dslice?currency=${data.ticker}`;
            openInSamePage(url);
            return;
        }
        if (data?.command === 'omon') {
            const url = `${process.env.NEXT_PUBLIC_UI_URL}/fx-options/omon?currency=${data.ticker}`;
            openInSamePage(url);
            return;
        }
        if (data?.command === 'ovdv') {
            const url = `${process.env.NEXT_PUBLIC_UI_URL}/fx-options/ovdv/${data.id}`;
            openInSamePage(url);
        }
        const sessionData = JSON.parse(sessionStorage.getItem('fxoInput'));
        if (error || data?.ticker !== sessionData?.inpData?.currencyPair) {
            sessionStorage.removeItem('fxoInput');
            return;
        }
        sessionStorage.setItem('fxoInput', JSON.stringify({ ...sessionData, currencyPair: data, origin: 'global_search' }));
        window.open(`${process.env.NEXT_PUBLIC_UI_URL}fx-options/form`, 'fxo_window', popupSpecsForDes);
        const pathArray = router.asPath.split('?')[0].split('/');
        if (pathArray[1] === 'dashboard') {
            setSearchPhrase('');
        } else {
            router.push('/dashboard');
        }
    };

    const onGetImm = ({ data, err, label }) => {
        if (err || label) return;
        setValidImm(data);
    };

    const openMeet = (data) => {
        if (data.error || data.tag !== 'search') return;
        const url = `${process.env.NEXT_PUBLIC_UI_URL}plot/irs/${data.irsId}?legs=${data.legs}&command=fwcm&meetingDate=${data.label}`;
        if (forDetailsPage) openInSamePage(url);
        else openTargetPage(url);
    }

    useEffect(() => {
        socketConnect();
        socket.on('/global-search', onResultsFetch);
        socket.on('/search/voice', onResultsFetch);
        socket.on('/plots/search', onPlotFetch);
        socket.on('/get/irs/meeting-date/:irsId', openMeet)
        socket.on('/basis-plots/search', onBasisPlotFetch);
        socket.on('/get/fx-options/currency-pair', onGetCurrencyPairFetch);
        socket.on('/get/irs', onAllIrsEvent);
        socket.on('/get/imm/valid', onGetImm);
        document.addEventListener('mousedown', handleClickOutside);
        socketEmit('/get/imm', { date: new Date(), n: 25, discardExpired: true });
        socketEmit('/get/irs');

        return () => {
            socket.off('/global-search', onResultsFetch);
            socket.off('/search/voice', onResultsFetch);
            socket.off('/plots/search', onPlotFetch);
            socket.off('/get/irs/meeting-date/:irsId', openMeet)
            socket.off('/basis-plots/search', onBasisPlotFetch);
            socket.off('/get/fx-options/currency-pair', onGetCurrencyPairFetch);
            socket.off('/get/irs', onAllIrsEvent);
            socket.off('/get/imm/valid', onGetImm);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const scrollElementToView = (id, start = false) => {
        const div = document.getElementById('search-dropdown');
        if (!div) return;
        const divRect = div.getBoundingClientRect();
        const elem = document.getElementById(id);
        if (elem) {
            const rect = elem.getBoundingClientRect();

            if (divRect.bottom < rect.bottom || divRect.top > rect.top) {
                elem.scrollIntoView({ behavior: 'smooth', block: start ? 'start' : 'nearest', inline: start ? 'nearest' : 'start' });
            }
        }
    };
    useEffect(() => {
        if (suggestions.length) {
            const timeoutId = setTimeout(() => {
                parentIndex.current = 0;
                childIndex.current = 0;
                const hash = `${allSuggestions[parentIndex.current]?.category}-${childIndex.current}`;
                setHighlightedId(hash);
                scrollElementToView(hash);
            }, 200);

            return () => clearTimeout(timeoutId);
        }
    }, [suggestions]);

    const onBasisPlotFetch = ({ data, error }) => {
        if (error || !data?.curves?.length) return;
        const quarterlyCurve = data.curves.find((c) => c.name.includes('3m'))?.id;
        const oisOr6mCurve = data.curves.find((c) => c.name.includes(data.isBasis6s3s ? '6m' : 'ois'))?.id;
        if (!oisOr6mCurve || !quarterlyCurve) return;
        if (data.isFwcm) {
            if (!quarterlyCurve) return;
            runCommandRef.current('ARC', {
                irsId: data.isBasis6s3s ? oisOr6mCurve : quarterlyCurve,
                [data.isBasis6s3s ? 'basis6s3sCurve' : 'bobCurve']: data.isBasis6s3s ? quarterlyCurve : oisOr6mCurve
            });
            return;
        }
        const conv = data.isBasis6s3s || (data.isBob && data.curves[0].currency === 'NZD') ? 'SAToQ' : ''; // AUD 6s3s
        const url = `${process.env.NEXT_PUBLIC_UI_URL}plot/irs/${data.isBasis6s3s ? oisOr6mCurve : quarterlyCurve}?legs=${data.tenors}${data.spot ? '&spot=1' : ''}${data.isFwd ? '&command=fwcm' : ''}${data.imm ? `&immFwd=${data.imm.label}` : ''}${data.thirdImm ? `&immLabel3=${data.thirdImm.label}` : ''}${data.plotType ? `&plotType=${data.plotType}` : ''}${data.secondImm ? `&imm=${data.secondImm.y}&immLabel=${data.secondImm.label}` : ''}${data.isBasis6s3s ? `&basis6s3sCurve=${quarterlyCurve}` : ''}${data.isBob ? `&bobCurve=${oisOr6mCurve}` : ''}${conv ? `&conv=${conv}` : ''}`;
        if (forDetailsPage) openInSamePage(url);
        else openTargetPage(url);
        setSearchPhrase('');
        resetCursor();
    };

    const onClick = (item) => {
        if (item?.command && eventCommands.includes(item?.command?.toUpperCase()) && (!['GP', 'HP', 'HS'].includes(item.command))) {
            const path = router.asPath.split('?')[0];
            const segments = path.split('/').filter(Boolean);
            trackCommandEvents(item.command, selectedCommand, segments[0], curve, 'Price', 'MAX');
        }

        try {
            if (!item) return;
            const baseUrl = process.env.NEXT_PUBLIC_UI_URL;
            const urlMap = {
                Bond: (bond) => `${baseUrl}bonds/${bond.id}?command=${selectedCommand}`,
                MaturedBond: (bond) => `${baseUrl}bonds/${bond.id}?command=${selectedCommand}`,
                Irs: (irsSecurity) => `${baseUrl}irs/${irsSecurity.id}?command=${selectedCommand}`,
                Index: (bond) => `${baseUrl}indexes/${bond.id}?command=${selectedCommand}`,
                Cds: (cds) => `${baseUrl}cds/${cds.id}?command=${selectedCommand}`,
                Equity: (equity) => `${baseUrl}equities/${equity.ticker}?command=${selectedCommand}`,
                User: (user) => `${baseUrl}users/${user.id}`,
                Trsy: `${baseUrl}trsy`,
                Ndf: (ndf) => `${baseUrl}currencies/${ndf.id}?command=${selectedCommand}`,
                Currency: (currency) => `${baseUrl}equities/${currency.ticker}?stockExchange=forex&command=${selectedCommand}`,
                QuickCurrency: (currency) => `${baseUrl}equities/${currency.ticker}?stockExchange=forex&command=${selectedCommand}`,
                Vol: (vol) => {
                    const query = new URLSearchParams({
                        command: selectedCommand,
                        ticker: vol.ticker
                    }).toString();
                    return `${baseUrl}vol/${vol.id}?${query}`;
                }
            };
            if (item.command === 'MEET') {
                runCommand('MEET', { irsId });
            }
            if (item.command === 'OTC') { runCommand('OTC'); }
            if (item.command === 'FWCM') { runCommand('FWCM', {}); }
            if (item.command === 'TRADES') { runCommand('TRADES'); }
            if (item.command === 'WATCHLIST') { openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}watchlist`); }
            if (item.command === 'CTASCAN') { openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}cta`); }
            if (item.command === 'OMON') {
                if (ovdvCurrency) {
                    socketEmit('/get/fx-options/currency-pair', { currency: ovdvCurrency, command: 'omon' });
                } else {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}/fx-options/omon?currency=USDINR`;
                    openInSamePage(url);
                }
                return;
            }
            if (item.command === 'DSLICE') {
                if (ovdvCurrency) {
                    socketEmit('/get/fx-options/currency-pair', { currency: ovdvCurrency, command: 'dslice' });
                } else {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}/fx-options/dslice?currency=USDINR`;
                    openInSamePage(url);
                }
                return;
            }
            if (item.command === 'EDGE') { runCommand('EDGE'); }
            if (item.command === 'ARC') { runCommand('ARC'); }
            if (item.command === 'STATS') { runCommand('STATS'); }
            if (item.type === 'Function') {
                if (item.group === 'Users') {
                    openInSamePage(urlMap.User(item));
                } else if (item.command === 'SHARE') {
                    takeScreenShot();
                } else if (item.command === 'PLOT' || item.command === 'REGRESS' || item.command === 'ROLL' || item.command === 'INP' || item.command === 'FWCM' || item.command === 'EDGE' || item.command === 'ARC' || item.command === 'OTC' || (item.command === 'CORP' && !isCorpChildComponent) || item.command === 'OVDV' || (!isBondPage && (item.command === 'BXT' || item.command === 'SXT')) || item.command === 'PLOTF' || item.command === 'PLOTA' || item.command === 'STATS' || item.command === '6s3s' || item.command === 'BOB' || item.command === 'CTASCAN') {
                    setSearchPhrase(`${item.command} `);
                } else if (item.command === 'GIP' && isIrsPage) {
                    runCommand('GIP');
                } else if (isIrsPage && item.command === 'BASIS') {
                    runCommand('BASIS', null);
                } else if (item.command === 'SDR' || item.command === 'ATLAS') {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}atlas`;
                    openInSamePage(url);
                } else if (item.command === 'TRACE') {
                    if (isIrsPage) runCommand('TRACE', { legs: irsTerm });
                    setSearchPhrase('TRACE ');
                } else if (item.command === 'CURVE') {
                    if (isIrsPage) runCommand('CURVE');
                } else if (item.command === 'ANCHOR') {
                    if (isIrsPage) runCommand('ANCHOR');
                } else if (item.command === 'REGRESS') {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}regress`;
                    openInSamePage(url);
                } else if (item.command === 'THOR') {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}thor`;
                    openInSamePage(url);
                } else if (item.command === 'OVDV') {
                    const name = ovdvCurrency;
                    if (name) return socketEmit('/get/fx-options/currency-pair', { currency: name, command: 'ovdv' });
                } else {
                    const commandSelected = item.command;
                    if (commandSelected === 'CORP') {
                        if (searchPhrase.toLowerCase().includes('corp')) {
                            const words = searchPhrase.trim().toLowerCase().split(/\s+/);
                            const ticker = words.find((word) => word !== 'corp');
                            if (!ticker) return;

                            if (isCorpChildComponent) {
                                setTicker(ticker);
                            } else {
                                if (ticker) {
                                    const url = `${process.env.NEXT_PUBLIC_UI_URL}corp/${ticker}`;
                                    openInSamePage(url);
                                    return;
                                }
                                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                                return;
                            }
                        }
                    }
                    if (['HP', 'GP'].includes(commandSelected) && (router.query.basis6s3sCurve || router.query.bobCurve)) {
                        onSelect(`basis${commandSelected}`);
                        return;
                    }
                    onSelect(commandSelected === 'RFQ' ? `${commandSelected}-${Date.now()}` : commandSelected);
                }
            } else if (forDetailsPage) {
                openInSamePage(urlMap[item.type](item));
            } else openTargetPage(urlMap[item.type](item));
            setShowSuggestions(false);
            setSuggestions([]);
            setCommandsAndUsers(options);
            resetCursor();
            !forDashboard && searchBoxRef.current.focus();
        } finally {
            const excludedCommands = ['PLOT', 'INP', 'FWCM', 'EDGE', 'OTC', 'OVDV', 'ROLL', 'REGRESS', 'THOR', 'TRACE', 'ARC', 'CURVE', 'PLOTF', 'PLOTA', 'ANCHOR', 'STATS', 'BOB', '6s3s', 'WATCHLIST', 'CTASCAN'];
            const bondExcludedCommands = ['BXT', 'SXT'];

            const isExcluded = excludedCommands.includes(item.command)
                || (!isCorpChildComponent && item.command === 'CORP')
                || (!isBondPage && bondExcludedCommands.includes(item.command));

            if (!isExcluded) {
                setSearchPhrase('');
            }
        }
    };
    const scrollElement = (id, start = false) => {
        const div = document.getElementById('search-dropdown');
        let scroll = false;
        if (!div) return scroll;
        const divRect = div.getBoundingClientRect();
        const elem = document.getElementById(id);
        if (elem) {
            const rect = elem.getBoundingClientRect();

            if (divRect.bottom < rect.bottom || divRect.top > rect.top) {
                elem.scrollIntoView({ behavior: 'smooth', block: start ? 'start' : 'end', inline: 'nearest' });
                scroll = true;
            }
        }
        return scroll;
    };
    const handleSelect = (number) => {
        if (number <= 0) {
            const hash = `${allSuggestions[parentIndex.current]?.category}-${childIndex.current}`;
            setHighlightedId(hash);
            scrollElement(hash, false);
            return;
        } if (childIndex.current < allSuggestions[parentIndex.current]?.items.length - 1) {
            childIndex.current += 1;
        } else if (parentIndex.current < allSuggestions?.length - 1) {
            parentIndex.current += 1;
            childIndex.current = 0;
        } else {
            parentIndex.current = 0;
            childIndex.current = 0;
        }
        handleSelect(number - 1);
    };
    const handleDown = (next) => {
        if (!next) return;
        if (childIndex.current < allSuggestions[parentIndex.current].items.length - 1) {
            childIndex.current += 1;
        } else if (parentIndex.current < allSuggestions.length - 1) {
            parentIndex.current += 1;
            childIndex.current = 0;
        } else {
            parentIndex.current = 0;
            childIndex.current = 0;
            next = false;
        }
        const hash = `${allSuggestions[parentIndex.current]?.category}-${childIndex.current}`;
        setHighlightedId(hash);
        const scroll = scrollElement(hash, true);
        if (!scroll) handleDown(next);
    };
    const handleUp = (next) => {
        if (!next) return;
        if (childIndex.current > 0) {
            childIndex.current -= 1;
        } else if (parentIndex.current > 0) {
            parentIndex.current -= 1;
            childIndex.current = allSuggestions[parentIndex.current].items.length - 1;
        } else {
            parentIndex.current = allSuggestions.length - 1;
            childIndex.current = allSuggestions[parentIndex.current].items.length - 1;
            next = false;
        }
        const hash = `${allSuggestions[parentIndex.current]?.category}-${childIndex.current}`;
        setHighlightedId(hash);
        const scroll = scrollElement(hash, false);
        if (!scroll) handleUp(next);
    };

    const handleF3KeyPress = () => {
        const searchInput = document.getElementById('global-search');
        if (searchInput) {
            searchInput.focus();

            if (!searchInput.value.toLowerCase().includes('corp')) {
                const value = `corp ${searchInput.value.trim()}`;
                searchInput.value = value;
                setSearchPhrase(value);
            }
        }
    };

    const handleKeyDown = (event) => {
        if (event.code === 'F3') {
            event.preventDefault();
            handleF3KeyPress();
            return;
        }
        if (allSuggestions.length && event.code === 'ArrowDown') {
            if (childIndex.current < allSuggestions[parentIndex.current]?.items.length - 1) {
                childIndex.current += 1;
            } else if (parentIndex.current < allSuggestions?.length - 1) {
                parentIndex.current += 1;
                childIndex.current = 0;
            } else {
                parentIndex.current = 0;
                childIndex.current = 0;
            }
            const hash = `${allSuggestions[parentIndex.current]?.category}-${childIndex.current}`;
            setHighlightedId(hash);
            scrollElementToView(hash);
        }
        if (allSuggestions.length && event.code === 'ArrowUp') {
            if (childIndex.current > 0) {
                childIndex.current -= 1;
            } else if (parentIndex.current > 0) {
                parentIndex.current -= 1;
                childIndex.current = allSuggestions[parentIndex.current].items.length - 1;
            } else {
                parentIndex.current = allSuggestions.length - 1;
                childIndex.current = allSuggestions[parentIndex.current].items.length - 1;
            }
            const hash = `${allSuggestions[parentIndex.current].category}-${childIndex.current}`;
            setHighlightedId(hash);
            scrollElementToView(hash);
        }
        if (event.code === 'Enter') {
            if (matchYasCommandWithPrice(searchPhrase.toLowerCase())) {
                const item = options.find((suggestion) => suggestion.category === 'Functions').items.find((item) => item.command === 'YAS');
                if (item) {
                    setYasPrice(getPriceFromYasWithPrice(searchPhrase.toLowerCase()));
                    onClick(item);
                    return;
                }
            }
            // IRS + FXO Enter (gip…meet, otc, ovdv/omon/dslice): useIrsGlobalSearchEnterCommand
            if (executeIrsEnterCommand(searchPhrase)) return;

            const { isValid: isValidInput } = parseInput(searchPhrase?.trim());
            const { isValid: isValidFxoInput } = parseFXOInput(searchPhrase?.trim(), true);

            if (childIndex.current === -1 && !isValidInput) return;
            const item = allSuggestions[parentIndex.current]?.items[childIndex.current];
            if (!item) {
                if (isValidFxoInput) {
                    const fxoInput = parseFXOInput(searchPhrase?.trim());
                    if (fxoInput?.isValid && fxoInput?.currencyPair) {
                        sessionStorage.setItem('fxoInput', JSON.stringify({ inpData: { ...fxoInput } }));
                        socketEmit('/get/fx-options/currency-pair', { currency: fxoInput.currencyPair });
                    }
                }
                if (!searchPhrase.toLowerCase().includes('plot') && isValidInput) {
                    const res = parseInput(searchPhrase?.trim());
                    const {
                        curveName, tenors, start, dv01, price, size
                    } = res;
                    let updatedTenors = tenors;
                    if (!curveName && !tenors?.length) {
                        const pathArray = router.asPath.split('?')[0].split('/');
                        if (pathArray[1] === 'irs' || pathArray[1] === 'currencies') {
                            sessionStorage.setItem(`${pathArray[2]}_input`, JSON.stringify({
                                curveName, term: tenors, start, dv01, price, size
                            }));
                            const url = new URL(window.location.href);
                            url.searchParams.set('inp', '1');
                            window.location.href = url.toString();
                            return;
                        }
                        if (isPlotPage) {
                            updatedTenors = legs;
                        } else {
                            return;
                        }
                    }
                    const phrase = curveName || curve;
                    socketEmit('/plots/search', {
                        phrase, tenors: updatedTenors, start, dv01, price, size, command: 'INP'
                    });
                    return;
                }
                return;
            }
            if (searchPhrase && (!['INP', 'PLOT', 'CORP', 'ROLL', 'REGRESS', 'THOR', 'PLOTF', 'PLOTA', '6s3s', 'BOB', 'WATCHLIST', 'CTASCAN'].includes(searchPhrase.trim()) || isCorpChildComponent)) {
                onClick(item);
            }
            setYasPrice(null);
            setShowSuggestions(false);
            resetCursor();
            forDashboard ? document.getElementById('global-search')?.blur() : searchBoxRef.current.focus();
        }
    };
    useEffect(() => {
        if (!globalCommand?.value) return;
        const commandText = globalCommand.value.toString().toLowerCase().trim();
        const functions = options.find((item) => item.category === 'Functions')?.items;
        const item = functions?.find((item) => item.command.toLowerCase() === commandText && item.group === 'Functions');

        if (globalCommand.type === 'command' && item) {
            setSearchPhrase('');
            if (item.command === 'SHARE') {
                takeScreenShot();
            } else {
                onSelect(item.command);
            }
        } else if (globalCommand.type === 'search') {
            searchBoxRef.current.focus();
            setSearchPhrase(commandText);
            debouncedInputHandler2.current({ phrase: commandText });
        } else if (globalCommand.type === 'scroll') {
            globalCommand.value === 'ArrowUp' ? handleUp(true) : handleDown(true);
        } else if (globalCommand.type === 'select') {
            parentIndex.current = 0;
            childIndex.current = -1;
            handleSelect(globalCommand.value);
        } else if (globalCommand.type === 'enter') {
            handleKeyDown({ code: 'Enter' });
        }
    }, [globalCommand]);

    useEffect(() => {
        if (isF3Entered) {
            handleF3KeyPress();
            setIsF3Entered(false);
        }
    }, [isF3Entered]);

    const getDisplayName = (item) => {
        if (item.type === 'Equity') return `${item.name} (${item.ticker})`;
        if (item.type === 'Currency') return `${item.ticker}`;
        if (item.type === 'QuickCurrency') return `${item.ticker}`;
        if (item.type === 'Function') return `${item.command} -`;
        return item.name;
    };

    const getDisplayType = (item) => {
        if (item.type === 'Ndf') return 'FX';
        if (item.type === 'QuickCurrency') return 'Currency';
        if (item.type === 'MaturedBond') return 'Bond';
        return item.type;
    };

    const getAdditionalInfo = (item) => {
        if (item.type === 'User') return `- ${item.companyName}`;
        if (item.type === 'Function') return `${item.label}`;
    };

    return <div className='relative' ref={inputRef}>
        {(enableVoiceCommand && (forDetailsPage || voiceCommand)) && <Dictaphone />}
        <SearchBox
            type='text'
            id='global-search'
            value={searchPhrase}
            placeholder={placeholder}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoComplete='off'
            inputClass={`${inputClass} cursor-text`}
            icon={icon || <ChevronRight size={18} className='text-success-600 relative top-1 left-0.5' />}
            iconClass={iconClass}
            onFocus={() => forDashboard && setShowSuggestions(true)}
            reference={searchBoxRef}
            isCommandLine={isCommandLine}
        />
        {showSuggestions && (
            <div id='search-dropdown' className={`absolute z-[9999] bg-gray-600 w-full rounded-md mt-0.5 overflow-y-auto max-h-96 ${globalSearchList} ${suggestionBg}`}
                onMouseEnter={resetCursor}>
                {allSuggestions.map((suggestion, index) => (
                    <div key={index} className="categoryBox">
                        <div className="px-3.5 py-1.5">
                            <Text variant="caption" className={`text-primary-100 ${categoryTitleClass}`}>
                                {suggestion.category}
                            </Text>
                        </div>
                        <div className="catList">
                            {suggestion.items.map((item, itemIndex) => (
                                <div id={`${suggestion.category}-${itemIndex}`}
                                    key={itemIndex}
                                    className={`flex justify-between group py-1.5 px-4 hover:bg-gray-500 cursor-pointer
                                        ${`${suggestion.category}-${itemIndex}` === highlightedId && `bg-gray-500 ${highlightBg}`} ${categoryList}`}
                                    onClick={() => onClick(item)}
                                >
                                    <Text
                                        variant="caption"
                                        className={`text-gray-100 font-medium flex items-center gap-1 group-hover:text-gray-100 break-keep ${suggestionName}`}
                                    >
                                        {getDisplayName(item)}
                                        {(item.type === 'User' || item.type === 'Function') && !item.formattingStyle
                                            && <span className='text-warning-200 text-xs font-medium capitalize'>{getAdditionalInfo(item)}</span>
                                        }
                                        {item.formattingStyle === 'html'
                                            && <span className='text-warning-200 text-xs font-medium capitalize'>{item.html}</span>
                                        }

                                    </Text>
                                    {item.type !== 'Function'
                                        && <Text variant="caption" className={`text-gray-100 uppercase group-hover:text-gray-100 whitespace-pre ml-2 ${suggestionName}`}>
                                            {getDisplayType(item)}
                                        </Text>}
                                </div>
                            ))}

                        </div>
                    </div>
                ))}
            </div>
        )
        }
    </div >;
};

export default GlobalSearch;
